import React, { useState, ReactNode, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Home, ChevronRight, ChevronLeft, BookOpen, Gamepad2, CheckCircle } from 'lucide-react';
import { ModuleSuccess } from './ModuleSuccess';
import { MODULE_SEQUENCE, COURSES } from '../../data/curriculum';
import { useSfx } from '../../store/sfxStore';
import { ErrorBoundary } from 'react-error-boundary';

// Define Fallback
const LessonErrorFallback = ({ error, resetErrorBoundary }: any) => {
    return (
        <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200 text-center my-8 shadow-xl max-w-2xl mx-auto">
            <h3 className="text-2xl font-black text-red-800 mb-2 font-comic tracking-tight">Oops! Something went wrong 🦍</h3>
            <p className="text-red-700 mb-6 bg-white p-4 rounded-xl border border-red-100 font-mono text-xs break-words text-left overflow-auto max-h-40 shadow-inner">
                {error.message || "Unknown error occurred"}
            </p>
            <button
                onClick={resetErrorBoundary}
                className="bg-red-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg hover:shadow-red-500/30 hover:scale-105 active:scale-95 flex items-center gap-2 mx-auto"
            >
                Try Again 🔄
            </button>
        </div>
    );
};

export interface LessonPage {
    id: number;
    title: string;
    icon: ReactNode;
    content: ReactNode;
}

interface LessonWrapperProps {
    moduleName: string;
    moduleEmoji: string;
    moduleColor: string; // e.g., 'blue', 'green', 'purple', 'red', 'orange', 'cyan'
    lessons: LessonPage[];
    children: ReactNode; // The game/activity component
    completed?: boolean;
    activityLabel?: string; // Default: "Game"
}

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', light: 'from-blue-100 to-cyan-50' },
    green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', light: 'from-green-100 to-emerald-50' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', light: 'from-purple-100 to-indigo-50' },
    red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', light: 'from-red-100 to-orange-50' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', light: 'from-orange-100 to-yellow-50' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200', light: 'from-cyan-100 to-blue-50' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', light: 'from-yellow-100 to-amber-50' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200', light: 'from-slate-100 to-gray-50' },
    pink: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200', light: 'from-pink-100 to-rose-50' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200', light: 'from-teal-100 to-cyan-50' },
};

export const LessonWrapper: React.FC<LessonWrapperProps> = ({
    moduleName,
    moduleEmoji,
    moduleColor,
    lessons,
    children,
    completed = false,
    activityLabel = "Activity"
}) => {
    const [lessonPage, setLessonPage] = useState(0);
    const [lessonCompleted, setLessonCompleted] = useState(false);
    const { play } = useSfx();

    // --- New Logic for Next Module ---
    const location = useLocation();
    const [nextModulePath, setNextModulePath] = useState<string | undefined>(undefined);

    useEffect(() => {
        try {
            // Safety check for data availability
            if (!MODULE_SEQUENCE || !COURSES) {
                console.warn('[LessonWrapper] Curriculum data missing');
                return;
            }

            // 1. Clean path (remove leading slash)
            const currentPath = location.pathname.replace(/^\/modules\/|^\//, '');
            console.log('[LessonWrapper] Current Path:', currentPath);

            // 2. Find Module Config by ID or Path
            let currentModuleId: string | null = null;

            for (const course of COURSES) {
                if (!course?.modules) continue;
                const found = course.modules.find(m => m.id === currentPath || m.path === currentPath);
                if (found) {
                    currentModuleId = found.id;
                    break;
                }
            }

            // 3. Find Next ID
            if (currentModuleId) {
                const currentIndex = MODULE_SEQUENCE.indexOf(currentModuleId as any);
                if (currentIndex !== -1 && currentIndex < MODULE_SEQUENCE.length - 1) {
                    const nextId = MODULE_SEQUENCE[currentIndex + 1];

                    // 4. Resolve Next Path
                    let nextPath: string = nextId;
                    // Look up config to see if it has a custom path
                    for (const course of COURSES) {
                        if (!course?.modules) continue;
                        const nextConfig = course.modules.find(m => m.id === nextId);
                        if (nextConfig) {
                            nextPath = nextConfig.path || nextConfig.id;
                            break;
                        }
                    }
                    console.log('[LessonWrapper] Next Path Resolved:', nextPath);
                    setNextModulePath(`/${nextPath}`);
                } else {
                    console.log('[LessonWrapper] No next module (end of sequence)');
                }
            } else {
                console.log('[LessonWrapper] Current module ID not found for path:', currentPath);
            }
        } catch (err) {
            console.error('[LessonWrapper] Error calculating next module:', err);
        }
    }, [location.pathname]);

    const colors = colorMap[moduleColor] || colorMap.blue;
    const currentLesson = lessons[lessonPage];

    const nextLesson = () => {
        if (lessonPage < lessons.length - 1) {
            setLessonPage(p => p + 1);
        } else {
            setLessonCompleted(true);
        }
    };

    const prevLesson = () => {
        if (lessonPage > 0) {
            setLessonPage(p => p - 1);
        }
    };

    const backToLessons = () => {
        setLessonCompleted(false);
        setLessonPage(0);
    };

    return (
        <div className={`min-h-screen bg-gradient-to-br ${colors.light} font-sans pb-20 pt-20`}>
            {/* Render Success Modal if Completed */}
            {completed && (
                <ModuleSuccess
                    title={moduleName}
                    nextModulePath={nextModulePath}
                // We don't implement onRetry here globally because it depends on the child's internal state logic.
                // But typically the child component shows its own "Play Again" button or users can use "Review Lessons".
                // The ModuleSuccess component is primarily for the "Next" navigation now.
                />
            )}

            {/* Header (Scaled Up) */}
            <div className="bg-white shadow-md px-8 py-6 flex justify-between items-center sticky top-20 z-30">
                <Link to="/" className="flex items-center gap-3 text-slate-500 font-bold hover:text-blue-600 transition-colors text-lg">
                    <Home size={24} /> <span className="hidden sm:inline">Map</span>
                </Link>
                <div className="flex items-center gap-4">
                    {completed && (
                        <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-base font-bold flex items-center gap-2 shadow-sm animate-pulse-slow">
                            <CheckCircle size={18} /> Done!
                        </div>
                    )}
                    <div className={`${colors.bg} ${colors.text} px-6 py-2 rounded-full text-base font-bold border-2 ${colors.border}`}>
                        <span className="text-xl mr-2">{moduleEmoji}</span> {moduleName}
                    </div>
                    {!lessonCompleted ? (
                        <div className="hidden md:flex bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-bold items-center gap-2 border border-purple-200">
                            <BookOpen size={16} /> Lesson {lessonPage + 1}/{lessons.length}
                        </div>
                    ) : (
                        <div className="hidden md:flex bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold items-center gap-2 border border-green-200">
                            <Gamepad2 size={16} /> {activityLabel}
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-4 md:p-8">
                {/* ============ LESSON PAGES ============ */}
                {!lessonCompleted ? (
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={lessonPage}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Lesson Header */}
                            <div className="text-center mb-10 mt-4">
                                <div className="text-6xl mb-6 filter drop-shadow-md">{currentLesson.icon}</div>
                                <h1 className="text-4xl md:text-5xl font-black font-serif text-slate-800 mb-4 tracking-tight">
                                    {currentLesson.title}
                                </h1>
                                {/* Progress Dots */}
                                <div className="flex justify-center gap-3 mt-6">
                                    {lessons.map((_, idx) => (
                                        <div
                                            key={idx}
                                            className={`w-4 h-4 rounded-full transition-all duration-300 ${idx === lessonPage
                                                ? 'bg-blue-600 scale-125 ring-4 ring-blue-200'
                                                : idx < lessonPage
                                                    ? 'bg-green-500'
                                                    : 'bg-slate-300'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Lesson Content - Card Container */}
                            <div className="bg-white rounded-[2rem] shadow-2xl p-8 md:p-12 mb-8 border border-white/50 backdrop-blur-lg">
                                <div className="prose prose-lg prose-slate max-w-none">
                                    {currentLesson.content}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            <div className="flex justify-between items-center mt-8">
                                <button
                                    onClick={() => { prevLesson(); play('click'); }}
                                    onMouseEnter={() => play('hover')}
                                    disabled={lessonPage === 0}
                                    className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-bold text-lg transition-all ${lessonPage === 0
                                        ? 'opacity-0 pointer-events-none'
                                        : 'bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl'
                                        }`}
                                >
                                    <ChevronLeft size={24} /> Back
                                </button>

                                <button
                                    onClick={() => { nextLesson(); play('click'); }}
                                    onMouseEnter={() => play('hover')}
                                    className="flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-xl bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
                                >
                                    {lessonPage === lessons.length - 1 ? (
                                        <>
                                            <Gamepad2 size={24} /> Start {activityLabel}
                                        </>
                                    ) : (
                                        <>
                                            Next <ChevronRight size={24} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                ) : (
                    /* ============ ACTIVITY/GAME MODE ============ */
                    <div>
                        <div className="mb-6 flex justify-between items-center">
                            <button
                                onClick={() => { backToLessons(); play('click'); }}
                                onMouseEnter={() => play('hover')}
                                className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                                <BookOpen size={16} /> Review Lessons
                            </button>
                        </div>

                        <ErrorBoundary FallbackComponent={LessonErrorFallback}>
                            {children}
                        </ErrorBoundary>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LessonWrapper;
